import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Select from "../../components/ui/Select";
import Switch from "../../components/ui/Switch";
import ReactSelect from "react-select";
import {
    createPromoCodeRequest,
    getPromoCodesRequest,
    updatePromoCodeRequest,
    getToursRequest
} from "../../services/api/event-content.api";
import {
    PromoCodeCreatePayload,
    PromoCodeItem,
    PromoCodeUpdatePayload,
    TourItem
} from "../../services/types/event-content.types";

interface PromoCodeFormState {
    code: string;
    discountValue: string;
    discountType: "percent" | "amount";
    startsAt: string;
    endsAt: string;
    usage_count: string;
    isActive: boolean;
    // Одноразовый промокод
    singleUse: boolean;
    tourIds: string[];
}

const initialFormState: PromoCodeFormState = {
    code: "",
    discountValue: "",
    discountType: "percent",
    startsAt: "",
    endsAt: "",
    usage_count: "",
    isActive: true,
    singleUse: false,
    tourIds: [],
};

interface ToursResponseShape {
    items?: TourItem[];
    data?: TourItem[];
    total?: number;
    page?: number;
    pagesize?: number;
    pages?: number;
}

const normalizeToursResponse = (
    response: TourItem[] | ToursResponseShape | null | undefined,
): TourItem[] => {
    if (Array.isArray(response)) {
        return response;
    }

    if (response?.items && Array.isArray(response.items)) {
        return response.items;
    }

    if (response?.data && Array.isArray(response.data)) {
        return response.data;
    }

    return [];
};

const formatDateTimeLocal = (value?: string | null): string => {
    if (!value) {
        return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const PromoCodesPage: React.FC = () => {
    const [items, setItems] = useState<PromoCodeItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<PromoCodeFormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [tours, setTours] = useState<TourItem[]>([]);

    useEffect(() => {
        const loadData = async () => {
            const [promoCodesResponse, toursResponse] = await Promise.all([
                getPromoCodesRequest(),
                getToursRequest({ page: 1, size: 200 }),
            ]);

            setItems(promoCodesResponse);
            setTours(Array.isArray(toursResponse) ? toursResponse : toursResponse.items ?? toursResponse.data ?? []);
        };

        void loadData();
    }, []);

    const toggleTour = (tourId: string) => {
        setFormState((prev) => ({
            ...prev,
            tourIds: prev.tourIds.includes(tourId)
                ? prev.tourIds.filter((id) => id !== tourId)
                : [...prev.tourIds, tourId],
        }));
    };
    const tourOptions = tours.map((tour) => ({
        value: tour.id,
        label: tour.name_ru || tour.name_en || tour.name_de || tour.id,
    }));

    const selectedTourOptions = tourOptions.filter((option) =>
        (formState.tourIds ?? []).includes(option.value),
    );
    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            return (
                item.code.toLowerCase().includes(query) ||
                item.discount_type.toLowerCase().includes(query)
            );
        });
    }, [items, searchValue]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getPromoCodesRequest();
            setItems(response);

            if (selectedId) {
                const selectedItem = response.find((item) => item.id === selectedId);

                if (!selectedItem) {
                    setSelectedId(null);
                    setFormState(initialFormState);
                }
            }
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить промокоды.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: PromoCodeItem) => {
        setSelectedId(item.id);
        setFormState({
            code: item.code,
            tourIds: item.tour_ids,
            discountValue: String(item.discount_value),
            usage_count: String(item.usage_count),
            discountType: item.discount_type,
            startsAt: formatDateTimeLocal(item.starts_at),
            endsAt: formatDateTimeLocal(item.ends_at),
            isActive: item.is_active,
            singleUse: Boolean(item.single_use),
        });
        setFormError(null);
        setSuccessMessage(null);
    };

    const parseTourIds = (value: string): string[] => {
        return value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormError(null);
        setSuccessMessage(null);

        if (!formState.code.trim()) {
            setFormError("Код промокода обязателен.");
            return;
        }

        if (!formState.discountValue.trim()) {
            setFormError("Укажи размер скидки.");
            return;
        }

        const discountValue = Number(formState.discountValue);

        if (Number.isNaN(discountValue)) {
            setFormError("Размер скидки должен быть числом.");
            return;
        }

        // const normalizedTourIds = tourids: formState.tourIds,;
        const startsAt = formState.startsAt ? new Date(formState.startsAt).toISOString() : null;
        const endsAt = formState.endsAt ? new Date(formState.endsAt).toISOString() : null;
        // const sortValue = formState.sort.trim() ? Number(formState.sort) : null;

        // if (formState.sort.trim() && Number.isNaN(sortValue)) {
        //     setFormError("Sort должен быть числом.");
        //     return;
        // }

        setIsSaving(true);

        try {
            if (selectedId) {
                const payload = {
                    code: formState.code.trim(),
                    discount_value: Number(formState.discountValue),
                    discount_type: formState.discountType,
                    starts_at: formState.startsAt || null,
                    ends_at: formState.endsAt || null,
                    is_active: formState.isActive,
                    single_use: formState.singleUse,
                    tour_ids: formState.tourIds,
                    sort: 0,
                };

                const updated = await updatePromoCodeRequest(selectedId, payload);

                setItems((prev) =>
                    prev.map((item) => (item.id === updated.id ? updated : item)),
                );
                handleSelect(updated);
                setSuccessMessage("Промокод обновлён.");
            } else {
                const payload = {
                    code: formState.code.trim(),
                    discount_value: Number(formState.discountValue),
                    discount_type: formState.discountType,
                    starts_at: formState.startsAt || null,
                    ends_at: formState.endsAt || null,
                    is_active: formState.isActive,
                    single_use: formState.singleUse,
                    tour_ids: formState.tourIds,
                    sort: 0,
                };

                const created = await createPromoCodeRequest(payload);

                setItems((prev) => [created, ...prev]);
                handleSelect(created);
                setSuccessMessage("Промокод создан.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось сохранить промокод.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Промокоды"
                description="Управление промокодами, скидками и периодом действия."
                actions={<Button onClick={resetForm}>Новый промокод</Button>}
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список промокодов</h3>
                            <p className="card-text">
                                Выбери существующий промокод или создай новый.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по коду..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? <div className="form-global-error">{pageError}</div> : null}

                    {isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Промокоды не найдены</h3>
                            <p className="empty-block-text">
                                Создай первый промокод или измени строку поиска.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Код</th>
                                    <th>Скидка</th>
                                    <th>Тип</th>
                                    <th>Кол-во исп.</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={
                                            item.id === selectedId ? "data-table-row-active" : ""
                                        }
                                        onClick={() => handleSelect(item)}
                                    >
                                        <td>{item.code}</td>
                                        <td>{item.discount_value}</td>
                                        <td>{item.discount_type}</td>
                                        <td>{item.usage_count}</td>
                                        <td>
                                                <span
                                                    className={
                                                        item.is_active
                                                            ? "status-badge status-badge-active"
                                                            : "status-badge status-badge-inactive"
                                                    }
                                                >
                                                    {item.is_active ? "Активен" : "Неактивен"}
                                                </span>
                                                {item.single_use ? (
                                                    <span
                                                        className="status-badge status-badge-inactive"
                                                        style={{ marginLeft: 6 }}
                                                        title={
                                                            item.is_used_up
                                                                ? "Одноразовый промокод уже использован"
                                                                : "Одноразовый промокод, ещё не использован"
                                                        }
                                                    >
                                                        {item.is_used_up ? "Использован" : "Одноразовый"}
                                                    </span>
                                                ) : null}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование промокода" : "Новый промокод"}
                            </h3>
                            <p className="card-text">
                                Заполни код, размер скидки, tour ids и даты активности.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Code" htmlFor="promo-code-code">
                                <Input
                                    id="promo-code-code"
                                    value={formState.code}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            code: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Discount value" htmlFor="promo-code-discount-value">
                                <Input
                                    id="promo-code-discount-value"
                                    type="number"
                                    value={formState.discountValue}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            discountValue: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Discount type" htmlFor="promo-code-discount-type">
                                <Select
                                    id="promo-code-discount-type"
                                    value={formState.discountType}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            discountType: event.target.value as "percent" | "amount",
                                        }))
                                    }
                                    options={[
                                        { value: "percent", label: "percent" },
                                        { value: "amount", label: "amount" },
                                    ]}
                                />
                            </Field>

                        </div>

                        <Field label="Мероприятия" htmlFor="promo-code-tours">
                            <ReactSelect
                                inputId="promo-code-tours"
                                isMulti
                                options={tourOptions}
                                value={selectedTourOptions}
                                onChange={(selectedOptions) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        tourIds: (selectedOptions ?? []).map((option) => option.value),
                                    }))
                                }
                                placeholder="Выберите мероприятия"
                                noOptionsMessage={() => "Ничего не найдено"}
                                closeMenuOnSelect={false}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: 52,
                                        borderRadius: 14,
                                        backgroundColor: "rgba(15, 23, 42, 0.72)",
                                        borderColor: state.isFocused ? "#6d7cff" : "rgba(148, 163, 184, 0.16)",
                                        boxShadow: "none",
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: "#0f172a",
                                        border: "1px solid rgba(148, 163, 184, 0.16)",
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isFocused ? "rgba(109, 124, 255, 0.16)" : "#0f172a",
                                        color: "#e5e7eb",
                                    }),
                                    multiValue: (base) => ({
                                        ...base,
                                        backgroundColor: "rgba(109, 124, 255, 0.16)",
                                    }),
                                    multiValueLabel: (base) => ({
                                        ...base,
                                        color: "#e5e7eb",
                                    }),
                                    multiValueRemove: (base) => ({
                                        ...base,
                                        color: "#e5e7eb",
                                        ":hover": {
                                            backgroundColor: "rgba(239, 68, 68, 0.2)",
                                            color: "#fff",
                                        },
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        color: "#e5e7eb",
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        color: "#e5e7eb",
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        color: "#94a3b8",
                                    }),
                                }}
                            />
                        </Field>


                        <div className="form-grid form-grid-two-columns">
                            <Field label="Starts at" htmlFor="promo-code-starts-at">
                                <Input
                                    id="promo-code-starts-at"
                                    type="datetime-local"
                                    value={formState.startsAt}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            startsAt: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Ends at" htmlFor="promo-code-ends-at">
                                <Input
                                    id="promo-code-ends-at"
                                    type="datetime-local"
                                    value={formState.endsAt}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            endsAt: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        isActive: checked,
                                    }))
                                }
                                label={formState.isActive ? "Активен" : "Неактивен"}
                            />
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.singleUse}
                                onChange={(checked) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        singleUse: checked,
                                    }))
                                }
                                label={
                                    formState.singleUse
                                        ? "Одноразовый (только один раз)"
                                        : "Многоразовый"
                                }
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? (
                            <div className="form-global-success">{successMessage}</div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохранение..."
                                    : selectedId
                                        ? "Сохранить изменения"
                                        : "Создать промокод"}
                            </Button>

                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Сбросить
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default PromoCodesPage;
